import React, { useState, useEffect, useCallback, createRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { List, Map } from 'immutable';
import { TransitionGroup, CSSTransition } from "react-transition-group";
import ReactGA from 'react-ga';

import { Start, AssessmentPartOne, AssessmentPartTwo, ReadyPartOne, ReadyPartTwo, ReportComplete, Report, AssessmentPartTwoModal } from '../components/mind-pattern'
import LoadingIndicator from '../components/loading-indicator'
import { MIND_PATTERNS_DATA_AREAS, MIND_PATTERNS_DATA_OPTIONS, MIND_PATTERNS_DATA_REPORTS, mind_pattern_sections } from '../../core/constants'
import { mindPatternsDataActions, getMindPatternsDataById } from '../../core/mind-patterns';
import { mindPatternsStoreOptionsActions } from '../../core/mind-patterns-storeOptions/actions';
import { mindPatternsStoreMPActions } from '../../core/mind-patterns-storemp/actions';
import { userActions } from '../../core/users';
import { trackingActions } from '../../core/tracking';
import { useNavigate } from 'react-router-dom';

const MindPatternPage = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const references = {
    start: createRef(null),
    ready: createRef(null),
    readyPartTwo: createRef(null),
    assessment: createRef(null),
    assessmentPartTwo: createRef(null),
    reportComplete: createRef(null),
    report: createRef(null)
  }

  const [currentStep, setCurrentStep] = useState(0);
  const [currentSection, setCurrentSection] = useState('');
  const [nextStep, setNextStep] = useState(false);
  const [direction, setDirection] = useState('right');
  const [animationClass, setAnimationClass] = useState({
    appearActive: 'animate__animated animate__slideInRight',
    enter: 'animate__animated',
    enterActive: 'animate__animated animate__slideInRight',
    exit: 'animate__animated',
    exitActive: 'animate__animated animate__slideOutLeft position-absolute top-0 left-0',
  });
  const [responseList, setResponseList] = useState(new Map());
  const [responseQuestionsTwo, setResponseQuestionsTwo] = useState(new List());
  const [responseQuestionsTwoUnsaved, setResponseQuestionsTwoUnsaved] = useState(false);
  const [mindPatternId, setMindPatternId] = useState(null);
  const [retake, setRetake] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const storeOptions = React.useRef([]);

  const user = useSelector(state => props.user || state.user);
  const mindPatternsStoreOptions = useSelector(state => state.mind_patterns_store_data_options);
  const areas = useSelector(state => getMindPatternsDataById(state, MIND_PATTERNS_DATA_AREAS));
  const options = useSelector(state => getMindPatternsDataById(state, MIND_PATTERNS_DATA_OPTIONS));
  const reports = useSelector(state => getMindPatternsDataById(state, MIND_PATTERNS_DATA_REPORTS));

  const viewMode = props.user ? true : false;
  const animation = props.animation !== undefined ? props.animation : true;
  const displayLoadingIndicator = user.isPending ||
    (areas ? areas.isPending : false) ||
    (options ? options.isPending : false) ||
    (reports ? reports.isPending : false);

  const setRetakeValue = useCallback((value) => {
    storeOptions.current = [];
    setRetake(value);
  }, []);

  const handleSaveMindPattern = useCallback((mind_pattern) => {
    const action = retake ? 'Retake assessment completed' : 'Creation completed';

    setResponseQuestionsTwoUnsaved(false);
    dispatch(mindPatternsStoreMPActions.mindPatternsSaveStoreMp(mind_pattern));
    dispatch(userActions.currentUser());

    // GA
    ReactGA.event({
      category: 'Mind Patterns',
      action
    });

    // Our tracking
    dispatch(trackingActions.saveTrackingEvent({
      category: 'Mind Patterns',
      action
    }));

    navigate('/home')
  }, [dispatch, retake, navigate]);

  const changeUnsavedMindPattern = useCallback((mind_pattern) => {
    setResponseQuestionsTwo(List(mind_pattern.split('')));
    setResponseQuestionsTwoUnsaved(true);
  }, []);

  const hanndleQuestionResponse = useCallback((next, data, value, response_type) => {
    if (response_type === mind_pattern_sections.ASSESSMENT) {
      const item = { number: data.id, value };
      const newResponseList = responseList.set(data.id, item);
      setResponseList(newResponseList);

      storeOptions.current.push(item);
      if (next === 'READY_PART_TWO') {
        dispatch(mindPatternsStoreOptionsActions.mindPatternsSaveStoreOptions(storeOptions.current));
        setResponseQuestionsTwo(new List());
      }
    }

    if (response_type === mind_pattern_sections.ASSESSMENT_PART_TWO) {
      const newResponseQuestionsTwo = responseQuestionsTwo.push(value);
      setResponseQuestionsTwo(newResponseQuestionsTwo);

      if (next === mind_pattern_sections.REPORT) {
        if (retake && user.mind_pattern) {
          const new_mind_pattern = newResponseQuestionsTwo.toJSON().join('');
          handleSaveMindPattern(new_mind_pattern);
        } else {
          setResponseQuestionsTwoUnsaved(true);
        }
      }

      if (newResponseQuestionsTwo.size === 2) {
        setIsOpenModal(true);
      }
    }

    hanndleNext(next);
  }, [responseList, responseQuestionsTwo, retake, user.mind_pattern, handleSaveMindPattern, dispatch]);

  const handleLastAnswerQuestionnaireTwo = useCallback(() => {
    const lastEnableOption = areas.items.find(item => !responseQuestionsTwo.includes(item.code));

    hanndleQuestionResponse(mind_pattern_sections.REPORT, null, lastEnableOption.code, mind_pattern_sections.ASSESSMENT_PART_TWO);
  }, [areas, responseQuestionsTwo, hanndleQuestionResponse]);

  const handlePreviousNavigationAction = useCallback((section, data, value, response_type) => {
    if (response_type === mind_pattern_sections.ASSESSMENT && value) {
      const item = { number: data.id, value };
      const newResponseList = responseList.set(data.id, item);
      setResponseList(newResponseList);
      storeOptions.current.push(item);
    }

    hanndlePrev(section);
  }, [responseList]);

  const hanndleNext = useCallback((section, id = null) => {
    const newAnimationClass = {
      appearActive: 'animate__animated animate__slideInRight',
      enter: 'animate__animated',
      enterActive: 'animate__animated animate__slideInRight',
      exit: 'animate__animated',
      exitActive: 'animate__animated animate__slideOutLeft position-absolute top-0 left-0',
    };

    if (section === mind_pattern_sections.READY_PART_TWO || section === mind_pattern_sections.REPORT) {
      setCurrentStep(0);
    }

    setCurrentSection(section);
    setDirection('right');
    setNextStep(true);
    setAnimationClass(newAnimationClass);
    setMindPatternId(id);
  }, [currentStep]);

  const hanndlePrev = useCallback((section) => {
    const newAnimationClass = {
      appearActive: 'animate__animated animate__slideInLeft',
      enter: 'animate__animated',
      enterActive: 'animate__animated animate__slideInLeft',
      exit: 'animate__animated',
      exitActive: 'animate__animated animate__slideOutLeft position-absolute top-0 left-0',
    };

    const newResponseQuestionsTwo = responseQuestionsTwo.pop();
    setDirection('left');
    setNextStep(true);
    setCurrentSection(section);
    setAnimationClass(newAnimationClass);
    setResponseQuestionsTwo(newResponseQuestionsTwo);
  }, [responseQuestionsTwo]);

  useEffect(() => {
    dispatch(mindPatternsDataActions.loadMindPatternsData(MIND_PATTERNS_DATA_REPORTS));
    dispatch(mindPatternsDataActions.loadMindPatternsData(MIND_PATTERNS_DATA_AREAS));
    dispatch(mindPatternsDataActions.loadMindPatternsData(MIND_PATTERNS_DATA_OPTIONS));

    if (user.mind_pattern) {
      setCurrentSection(mind_pattern_sections.REPORT);
    } else {
      setCurrentSection(mind_pattern_sections.START);
    }
  }, [dispatch, user.mind_pattern]);

  useEffect(() => {
    // Handle step navigation
    console.log(currentSection)
    if (!nextStep) return;

    if (currentSection === mind_pattern_sections.ASSESSMENT || currentSection === mind_pattern_sections.ASSESSMENT_PART_TWO) {
      if (direction === 'right') {
        setCurrentStep(prev => prev + 1);
      } else if (direction === 'left') {
        setCurrentStep(prev => prev - 1);
      }

      setNextStep(false);
    }

    if (currentSection === mind_pattern_sections.READY) {
      dispatch(mindPatternsDataActions.loadMindPatternsData(MIND_PATTERNS_DATA_OPTIONS));
      setNextStep(false);
    }

    if (currentSection === mind_pattern_sections.READY_PART_TWO) {
      dispatch(mindPatternsDataActions.loadMindPatternsData(MIND_PATTERNS_DATA_AREAS));
      setNextStep(false);
    }
  }, [currentSection, nextStep, direction, dispatch]);

  useEffect(() => {
    if (
      mindPatternsStoreOptions.items.result === 1 &&
      mindPatternsStoreOptions.items.mp
    ) {
      changeUnsavedMindPattern(mindPatternsStoreOptions.items.mp);
      if (
        currentSection !== mind_pattern_sections.REPORT &&
        currentSection !== mind_pattern_sections.REPORT_COMPLETE
      ) {
        setCurrentSection(mind_pattern_sections.REPORT);
      }
    }
  }, [mindPatternsStoreOptions, currentSection, changeUnsavedMindPattern]);

  useEffect(() => {
    if (currentSection === mind_pattern_sections.REPORT && !responseQuestionsTwoUnsaved) {
      dispatch(userActions.currentUser());
    }
  }, [currentSection, responseQuestionsTwoUnsaved, dispatch]);

  const attentionTypes = ['Focused attention', 'Sorting attention', 'Open attention'];

  let report = null;
  if (currentSection === mind_pattern_sections.REPORT) {
    if (mindPatternsStoreOptions.items.result === 1 && !user.mind_pattern) {
      report = (
        <Report
          attentionTypes={attentionTypes}
          areas={areas}
          hanndleNext={hanndleNext}
          reports={reports.items}
          viewMode={viewMode}
          setRetake={setRetakeValue}
          mind_pattern_unsaved={responseQuestionsTwoUnsaved}
          handleSaveMindPattern={handleSaveMindPattern}
          selected_options={storeOptions.current}
          mind_pattern={responseQuestionsTwo.toJSON().join('') || mindPatternsStoreOptions.items.user.mind_pattern}
        />
      );
    } else if (user.mind_pattern || responseQuestionsTwoUnsaved) {
      report = (
        <Report
          attentionTypes={attentionTypes}
          areas={areas}
          hanndleNext={hanndleNext}
          reports={reports.items}
          viewMode={viewMode}
          setRetake={setRetakeValue}
          mind_pattern_unsaved={responseQuestionsTwoUnsaved}
          handleSaveMindPattern={handleSaveMindPattern}
          mind_pattern={responseQuestionsTwoUnsaved ? responseQuestionsTwo.toJSON().join('') : user.mind_pattern}
        />
      );
    } else {
      report = (
        <Report
          attentionTypes={attentionTypes}
          areas={areas}
          hanndleNext={hanndleNext}
          reports={reports.items}
          viewMode={viewMode}
          setRetake={setRetakeValue}
          mind_pattern_unsaved={responseQuestionsTwoUnsaved}
          handleSaveMindPattern={handleSaveMindPattern}
          selected_options={responseQuestionsTwo}
        />
      );
    }
  }

  const handleExited = useCallback(() => {
  }, []);

  return (
    <>
      <div className="container-fluid h-100">
        <div className="view overflow-hidden mpattern-content h-100 position-relative">
          <TransitionGroup component={null}>
            {currentSection === mind_pattern_sections.START && (
              <CSSTransition
                nodeRef={references.start}
                classNames={animation ? animationClass : ''}
                timeout={1000}
                appear
                in={currentSection === mind_pattern_sections.START}
                onExited={handleExited}
              >
                <div ref={references.start}>
                  <Start
                    hanndleNext={hanndleNext}
                    user={user}
                    retake={retake}
                    areas={areas}
                  />
                </div>
              </CSSTransition>
            )}

            {currentSection === mind_pattern_sections.READY && (
              <CSSTransition
                nodeRef={references.ready}
                classNames={animation ? animationClass : ''}
                timeout={1000}
                appear
                in={currentSection === mind_pattern_sections.READY}
                onExited={handleExited}
              >
                <div ref={references.ready}>
                  <ReadyPartOne
                    hanndleNext={hanndleNext}
                    retake={retake}
                  />
                </div>
              </CSSTransition>
            )}

            {currentSection === mind_pattern_sections.ASSESSMENT && (
              <CSSTransition
                nodeRef={references.assessment}
                classNames={animation ? animationClass : ''}
                timeout={1000}
                appear
                in={currentSection === mind_pattern_sections.ASSESSMENT}
                onExited={handleExited}
              >
                <div ref={references.assessment}>
                  <AssessmentPartOne
                    hanndleNext={hanndleQuestionResponse}
                    hanndlePrev={(data, value, response_type) =>
                      handlePreviousNavigationAction(currentSection, data, value, response_type)
                    }
                    questions={options.items}
                    current_step={currentStep}
                    current_list_response={responseList}
                  />
                </div>
              </CSSTransition>
            )}

            {currentSection === mind_pattern_sections.READY_PART_TWO && (
              <CSSTransition
                nodeRef={references.readyPartTwo}
                classNames={animation ? animationClass : ''}
                timeout={1000}
                appear
                in={currentSection === mind_pattern_sections.READY_PART_TWO}
                onExited={handleExited}
              >
                <div ref={references.readyPartTwo}>
                  <ReadyPartTwo
                    areas={areas}
                    hanndleNext={hanndleNext}
                  />
                </div>
              </CSSTransition>
            )}

            {currentSection === mind_pattern_sections.ASSESSMENT_PART_TWO && (
              <CSSTransition
                nodeRef={references.assessmentPartTwo}
                classNames={animation ? animationClass : ''}
                timeout={1000}
                appear
                in={currentSection === mind_pattern_sections.ASSESSMENT_PART_TWO}
                onExited={handleExited}
              >
                <div ref={references.assessmentPartTwo}>
                  <AssessmentPartTwo
                    hanndleNext={hanndleQuestionResponse}
                    hanndlePrev={hanndlePrev}
                    questions={areas.items}
                    current_step={currentStep}
                    selected_options={responseQuestionsTwo}
                  />
                </div>
              </CSSTransition>
            )}

            {currentSection === mind_pattern_sections.REPORT_COMPLETE && (
              <CSSTransition
                nodeRef={references.reportComplete}
                classNames={animation ? animationClass : ''}
                timeout={1000}
                appear
                in={currentSection === mind_pattern_sections.REPORT_COMPLETE}
                onExited={handleExited}
              >
                <div ref={references.reportComplete}>
                  <ReportComplete
                    attentionTypes={attentionTypes}
                    areas={areas}
                    hanndleNext={hanndleNext}
                    mind_pattern_id={mindPatternId}
                    current_mind_pattern={user.mind_pattern || responseQuestionsTwo.toJSON().join('')}
                    changeUnsavedMindPattern={changeUnsavedMindPattern}
                    mind_pattern_unsaved={responseQuestionsTwoUnsaved}
                  />
                </div>
              </CSSTransition>
            )}

            {currentSection === mind_pattern_sections.REPORT && (
              <CSSTransition
                nodeRef={references.report}
                classNames={animation ? animationClass : ''}
                timeout={1000}
                appear
                in={currentSection === mind_pattern_sections.REPORT}
                onExited={handleExited}
              >
                <div ref={references.report}>
                  {report}
                </div>
              </CSSTransition>
            )}
          </TransitionGroup>
        </div>
      </div>

      {(displayLoadingIndicator || (user.checked && !user.id)) && <LoadingIndicator />}

      <AssessmentPartTwoModal
        open={isOpenModal}
        setOpen={setIsOpenModal}
        callback={handleLastAnswerQuestionnaireTwo}
      />
    </>
  );
};

export default MindPatternPage;